// src/components/AdminIDCardPage.js
import React, { useEffect, useState } from 'react';
import API from '../services/api';
import {
  Container,
  Table,
  Button,
  Spinner,
  Alert,
  Modal
} from 'react-bootstrap';
import './AdminIDCardPage.css';
import { formatEmployeeId } from '../utils/idFormat';
import { buildEmployeeQrUrl } from '../utils/idCardQr';

const AdminIDCardPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [coolingPeriodDays, setCoolingPeriodDays] = useState(15);

  const getDaysSince = (dateLike) => {
    if (!dateLike) return null;
    const t = new Date(dateLike).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  };

  // prefer joiningDate -> approvedAt -> createdAt
  const pickStartDate = (e) => e?.joiningDate || e?.approvedAt || e?.createdAt || null;

  const resolvePhoto = (photo) => {
    if (!photo) return "https://via.placeholder.com/160";
    if (/^https?:\/\//i.test(photo) || photo.startsWith("data:") || photo.startsWith("blob:")) {
      return photo;
    }
    const base = process.env.REACT_APP_API_URL || "";
    return `${base}${photo}`;
  };

  const buildEmployeeId = (e) => formatEmployeeId(e);
  const buildIdCardHtml = (e) => `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Employee ID Card</title>
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 24px; font-family: "Arial", sans-serif; background: #f1f5f9; }
          .card {
            width: 340px;
            margin: 0 auto;
            background: #0f172a;
            color: #fff;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 20px 40px rgba(15, 23, 42, 0.3);
          }
          .head { padding: 14px 16px; background: #111827; font-weight: 700; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; }
          .body { padding: 16px; text-align: center; }
          .photo {
            width: 110px; height: 110px; border-radius: 50%;
            margin: 0 auto 12px;
            background: #1f2937 center/cover no-repeat;
            border: 3px solid rgba(255,255,255,0.2);
          }
          .name { font-size: 18px; font-weight: 800; margin-bottom: 4px; }
          .role { font-size: 12px; color: #cbd5f5; margin-bottom: 12px; }
          .row { display: flex; justify-content: space-between; font-size: 12px; padding: 6px 0; border-bottom: 1px solid rgba(255,255,255,0.08); }
          .row:last-child { border-bottom: 0; }
          .label { color: #94a3b8; }
          .footer { padding: 12px 16px; background: #0b1220; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #cbd5f5; }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="head">Lotus Rice Mill Pvt. Ltd.</div>
          <div class="body">
            <div class="photo" style="background-image:url('${resolvePhoto(e.photo)}');"></div>
            <div class="name">${e.fullName || "—"}</div>
            <div class="role">${e.designation || e.employeeRole?.name || "Employee"}</div>
            <div class="row"><span class="label">Employee ID</span><span>${buildEmployeeId(e)}</span></div>
            <div class="row"><span class="label">Mobile</span><span>${e.mobile || "—"}</span></div>
            <div class="row"><span class="label">Department</span><span>${e.department?.name || e.departmentName || e.department || "—"}</span></div>
            <div class="row"><span class="label">Status</span><span>${e.approved === false ? "Inactive" : "Active"}</span></div>
          </div>
          <div class="footer">Official Employee ID</div>
        </div>
      </body>
    </html>
  `;

  const openIdCard = (e, autoPrint = false) => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(buildIdCardHtml(e));
    w.document.close();
    if (autoPrint) {
      setTimeout(() => {
        w.focus();
        w.print();
      }, 300);
    }
  };

  const fetchEligibleEmployees = async () => {
    try {
      setLoading(true);
      setError('');
      // IMPORTANT: make sure BE is mounted at /api; else change this path
      const res = await API.get('/admin/idcard-eligibility');
      const rows = Array.isArray(res.data?.employees) ? res.data.employees : [];
      setEmployees(rows);
      setCoolingPeriodDays(Number(res.data?.coolingPeriodDays) > 0 ? Number(res.data.coolingPeriodDays) : 15);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (id) => {
    try {
      await API.put(`/admin/employees/${id}/generate-idcard`);
      fetchEligibleEmployees();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to generate ID Card');
    }
  };

  const handleReject = async (id) => {
    try {
      await API.put(`/admin/employees/${id}/reject-idcard`);
      fetchEligibleEmployees();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to reject ID Card');
    }
  };

  useEffect(() => {
    fetchEligibleEmployees();
  }, []);

  const handleAdminPhotoUpload = async () => {
    if (!photoFile || !selectedEmployee?._id) return;
    try {
      setPhotoUploading(true);
      setPhotoError("");
      const form = new FormData();
      form.append("photo", photoFile);
      const res = await API.put(`/admin/employees/${selectedEmployee._id}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelectedEmployee(res.data);
      setEmployees((prev) =>
        prev.map((e) => (e._id === res.data._id ? { ...e, photo: res.data.photo } : e))
      );
      setPhotoFile(null);
    } catch (err) {
      setPhotoError("Failed to upload photo");
    } finally {
      setPhotoUploading(false);
    }
  };

  const getStatusChip = (status) => {
    if (status === 'generated') return { label: 'Generated', tone: 'success' };
    if (status === 'rejected') return { label: 'Rejected', tone: 'danger' };
    return { label: 'Pending', tone: 'warning' };
  };

  const getTenureChip = (days) => {
    if (days == null) return { label: '—', tone: 'neutral' };
    if (days >= coolingPeriodDays) return { label: `${days} Days`, tone: 'success' };
    return { label: `${days} Days`, tone: 'warning' };
  };

  const getInitials = (name = '') =>
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0].toUpperCase())
      .join('');

  return (
    <Container className="mt-4 admin-idcard-page">
      <div className="admin-idcard-header">
        <div>
          <div className="admin-idcard-title-row">
            <h4 className="mb-0">Employee ID Card Management</h4>
            <span className="admin-idcard-count">{employees.length} total</span>
          </div>
          <p className="admin-idcard-subtitle">
            ID card auto-generation {coolingPeriodDays} din baad hoti hai, lekin admin portal se aap manually kabhi bhi generate kar sakte hain.
          </p>
        </div>
        <div className="admin-idcard-actions">
          <Button variant="outline-secondary" size="sm">Export CSV</Button>
          <Button variant="primary" size="sm">New Request</Button>
        </div>
      </div>

      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div className="admin-idcards-table">
            <div className="admin-idcard-table-card">
              <div className="admin-idcard-table-tabs">
                <div className="admin-idcard-tabs">
                  <span className="active">All Requests</span>
                  <span>Auto Eligible ({coolingPeriodDays}+ Days)</span>
                  <span>Pending Approval</span>
                  <span>Rejected</span>
                </div>
                <div className="admin-idcard-table-tools">
                  <span className="material-symbols-outlined">filter_list</span>
                  <span className="material-symbols-outlined">sort</span>
                </div>
              </div>
              <Table hover responsive className="admin-idcard-table">
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    <th>Contact Details</th>
                    <th>Joining Date</th>
                    <th>Tenure</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {employees.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">No employees found</td>
                    </tr>
                  ) : (
                    employees.map((e) => {
                      const start = pickStartDate(e);
                      const days = e.idCardDaysSinceStart ?? getDaysSince(start);
                      const tenure = getTenureChip(days);
                      const status = getStatusChip(e.idCardStatus);
                      const canGenerate = e.idCardStatus !== 'generated';
                      const autoEligible = e.idCardAutoEligible ?? (days != null && days >= coolingPeriodDays);
                      const coolingDaysRemaining =
                        e.idCardCoolingDaysRemaining ?? (days == null ? coolingPeriodDays : Math.max(coolingPeriodDays - days, 0));

                      return (
                        <tr key={e._id}>
                          <td>
                            <div className="admin-idcard-employee">
                              <div className="admin-idcard-avatar">{getInitials(e.fullName)}</div>
                              <div className="admin-idcard-employee-text">
                                <span className="admin-idcard-name">{e.fullName || '—'}</span>
                                <span className="admin-idcard-role">Employee</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="admin-idcard-contact">
                              <span>{e.mobile || '—'}</span>
                              <span>{e.email || '—'}</span>
                            </div>
                          </td>
                          <td className="admin-idcard-date">{start ? new Date(start).toLocaleDateString() : '—'}</td>
                          <td>
                            <span className={`admin-idcard-chip ${tenure.tone}`}>
                              <span className="dot" />
                              {tenure.label}
                            </span>
                            {!autoEligible && e.idCardStatus !== 'generated' ? (
                              <div className="small text-muted mt-1">
                                Auto in {coolingDaysRemaining} day{coolingDaysRemaining === 1 ? '' : 's'}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <span className={`admin-idcard-status ${status.tone}`}>{status.label}</span>
                          </td>
                          <td className="text-end">
                            <div className="admin-idcard-row-actions">
                              {e.idCardStatus === 'generated' ? (
                                <>
                                  <Button size="sm" className="admin-idcard-btn primary" onClick={() => setSelectedEmployee(e)}>
                                    View
                                  </Button>
                                  <Button size="sm" className="admin-idcard-btn dark" onClick={() => openIdCard(e, true)}>
                                    Download PDF
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    className="admin-idcard-btn success"
                                    onClick={() => handleGenerate(e._id)}
                                    disabled={!canGenerate}
                                  >
                                    {autoEligible ? 'Generate' : 'Generate Now'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="admin-idcard-btn danger"
                                    onClick={() => handleReject(e._id)}
                                    disabled={e.idCardStatus === 'rejected'}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          <div className="admin-idcards-cards">
            {employees.length === 0 ? (
              <div className="text-center">No employees found</div>
            ) : (
              employees.map((e) => {
                const start = pickStartDate(e);
                const days = e.idCardDaysSinceStart ?? getDaysSince(start);
                const canGenerate = e.idCardStatus !== 'generated';
                const autoEligible = e.idCardAutoEligible ?? (days != null && days >= coolingPeriodDays);
                const coolingDaysRemaining =
                  e.idCardCoolingDaysRemaining ?? (days == null ? coolingPeriodDays : Math.max(coolingPeriodDays - days, 0));
                return (
                  <div key={e._id} className="admin-idcard-card">
                    <div className="admin-idcard-top">
                      <div>
                        <div className="admin-idcard-name">{e.fullName || '—'}</div>
                        <div className="admin-idcard-sub">{e.mobile || '—'}</div>
                        <div className="admin-idcard-sub">{e.email || '—'}</div>
                      </div>
                      <div>
                        <span className={`admin-idcard-chip ${getTenureChip(days).tone}`}>
                          <span className="dot" />
                          {getTenureChip(days).label}
                        </span>
                      </div>
                    </div>
                    <div className="admin-idcard-row">
                      <span className="label">Start</span>
                      <span className="value">{start ? new Date(start).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className="admin-idcard-row">
                      <span className="label">Status</span>
                      <span className={`admin-idcard-status ${getStatusChip(e.idCardStatus).tone}`}>
                        {getStatusChip(e.idCardStatus).label}
                      </span>
                    </div>
                    {!autoEligible && e.idCardStatus !== 'generated' ? (
                      <div className="small text-muted mb-2">
                        Auto generation in {coolingDaysRemaining} day{coolingDaysRemaining === 1 ? '' : 's'}.
                      </div>
                    ) : null}
                    <div className="admin-idcard-actions">
                      {e.idCardStatus === 'generated' ? (
                        <>
                          <Button
                            size="sm"
                            className="admin-idcard-btn primary"
                            onClick={() => setSelectedEmployee(e)}
                          >
                            View
                          </Button>{' '}
                          <Button
                            size="sm"
                            className="admin-idcard-btn dark"
                            onClick={() => openIdCard(e, true)}
                          >
                            PDF
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            className="admin-idcard-btn success"
                            onClick={() => handleGenerate(e._id)}
                            disabled={!canGenerate}
                          >
                            {autoEligible ? 'Generate' : 'Generate Now'}
                          </Button>{' '}
                          <Button
                            size="sm"
                            className="admin-idcard-btn danger"
                            onClick={() => handleReject(e._id)}
                            disabled={e.idCardStatus === 'rejected'}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Preview Modal */}
      <Modal show={!!selectedEmployee} onHide={() => setSelectedEmployee(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Employee ID Card</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center">
          {selectedEmployee && (
            <div className="idcard-preview">
              <div className="idcard-shell">
                <div className="idcard-head">
                  <div className="idcard-headline">
                    <span className="material-symbols-outlined">verified_user</span>
                    <span>Official Digital Identity</span>
                  </div>
                </div>
                <div className="idcard-photo">
                  <div
                    className="idcard-photo-inner"
                    style={{
                      backgroundImage: `url(${resolvePhoto(selectedEmployee.photo)})`
                    }}
                  />
                </div>
                <div className="idcard-body">
                  <h3>{selectedEmployee.fullName || '—'}</h3>
                  <p className="idcard-role">{selectedEmployee.designation || selectedEmployee.employeeRole?.name || "Employee"}</p>
                  <div className="idcard-details">
                    <div className="idcard-detail-row">
                      <span>Employee ID</span>
                      <strong>{buildEmployeeId(selectedEmployee)}</strong>
                    </div>
                    <div className="idcard-detail-row">
                      <span>Mobile</span>
                      <strong>{selectedEmployee.mobile || '—'}</strong>
                    </div>
                    <div className="idcard-detail-row">
                      <span>Joined</span>
                      <strong>
                        {(() => {
                          const s = pickStartDate(selectedEmployee);
                          return s ? new Date(s).toLocaleDateString() : '—';
                        })()}
                      </strong>
                    </div>
                    <div className="idcard-detail-row">
                      <span>Department</span>
                      <strong>{selectedEmployee.department?.name || selectedEmployee.departmentName || selectedEmployee.department || '—'}</strong>
                    </div>
                    <div className="idcard-detail-row">
                      <span>Role</span>
                      <strong>{selectedEmployee.employeeRole?.name || selectedEmployee.role || '—'}</strong>
                    </div>
                    <div className="idcard-detail-row">
                      <span>Status</span>
                      <strong>{selectedEmployee.approved === false ? "Inactive" : "Active"}</strong>
                    </div>
                  </div>
                  <div className={`idcard-verified ${selectedEmployee.approved === false ? 'danger' : ''}`}>
                    {selectedEmployee.approved === false ? 'Terminated' : 'Verified Employee'}
                  </div>
                  <div className="idcard-footer">
                    <div className="idcard-brand">Lotus Rice Mill Pvt. Ltd.</div>
                    <div className="idcard-qr">
                      <img
                        src={buildEmployeeQrUrl(selectedEmployee, 128)}
                        alt={`QR for ${selectedEmployee.fullName || "employee"} ID card`}
                        className="idcard-qr-image"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  </div>
                </div>
                <div className="idcard-stripe" />
              </div>
              <div className="admin-idcard-photo-upload">
                {photoError && <div className="text-danger small mb-2">{photoError}</div>}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                />
                <Button
                  size="sm"
                  className="admin-idcard-btn primary"
                  onClick={handleAdminPhotoUpload}
                  disabled={!photoFile || photoUploading}
                >
                  {photoUploading ? "Uploading..." : "Upload Photo"}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminIDCardPage;
